import { INTESTAZIONE_UTENTE, utenteDichiarato } from '../lib/identitaDichiarata';

export class ErroreApi extends Error {
  constructor(
    message: string,
    readonly stato: number,
    readonly problemi?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ErroreApi';
  }
}

/*  Non autenticato o non autorizzato: due casi distinti da tutti gli altri errori.

    Servono distinti perché l'interfaccia ci fa due cose diverse. Un 401 non è un
    guasto — è «non so chi sei», e la risposta è una schermata di accesso, non un
    messaggio rosso. Un 403 è «non ti spetta», e va detto con la frase che manda il
    server, che nomina il permesso mancante.  */
export const NON_AUTENTICATO = 401;
export const VIETATO = 403;

export class ErroreRete extends Error {
  constructor() {
    super('Nessuna connessione: la richiesta non è partita.');
    this.name = 'ErroreRete';
  }
}

const BASE = '/api';

async function esegui<T>(percorso: string, opzioni: RequestInit): Promise<T> {
  let risposta: Response;
  try {
    /*  L'identità viaggia in un'intestazione su OGNI richiesta, anche le letture: il
        server autorizza tutto, non solo le scritture, e una lettura senza identità
        tornerebbe 401. Il valore c'è solo in sviluppo — con un'autenticazione vera
        l'identità sta nel cookie o nel token e questa riga non aggiunge niente.  */
    const dichiarato = utenteDichiarato();

    risposta = await fetch(`${BASE}${percorso}`, {
      ...opzioni,
      // `credentials` guarda avanti: un'autenticazione a cookie non funziona senza,
      // e in sviluppo il frontend sta su un'altra origine.
      credentials: 'include',
      headers: {
        /*  Con un FormData il Content-Type NON si scrive: lo mette il browser, e ci
            attacca il separatore delle parti. Scrivendolo a mano si manda
            «multipart/form-data» senza boundary, e il server risponde 400 su un
            caricamento perfettamente valido.  */
        ...(opzioni.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(dichiarato ? { [INTESTAZIONE_UTENTE]: dichiarato } : {}),
        ...(opzioni.headers ?? {}),
      },
    });
  } catch {
    // Distinguere "rete assente" da "errore del server" è ciò che permette
    // alla coda offline di sapere se ritentare o se scartare.
    throw new ErroreRete();
  }

  if (risposta.status === 204) return undefined as T;

  const testo = await risposta.text();
  const corpo = testo ? JSON.parse(testo) : null;

  if (!risposta.ok) {
    const problemi = corpo?.errors as Record<string, string[]> | undefined;
    // L'ordine conta: `detail` è dove finisce il messaggio del dominio
    // («Manca la data di consegna richiesta…»), `title` è la categoria generica.
    // Mostrare il titolo quando esiste il dettaglio significa buttare via la frase utile.
    const messaggio =
      (problemi && Object.values(problemi).flat().join(' ')) ||
      corpo?.messaggio ||
      corpo?.detail ||
      corpo?.title ||
      `Errore ${risposta.status}`;
    throw new ErroreApi(messaggio, risposta.status, problemi);
  }

  return corpo as T;
}

export const api = {
  get: <T>(percorso: string) => esegui<T>(percorso, { method: 'GET' }),
  post: <T>(percorso: string, corpo: unknown) =>
    esegui<T>(percorso, { method: 'POST', body: JSON.stringify(corpo) }),
  /*  PATCH c'è solo dove si aggiornano davvero dei campi. Le transizioni di stato —
      invia, annulla, approva — restano POST su un sottopercorso che le nomina: non sono
      aggiornamenti di un campo, e l'URL deve dire quale regola si sta applicando.  */
  put: <T>(percorso: string, corpo: unknown) =>
    esegui<T>(percorso, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corpo),
    }),
  patch: <T>(percorso: string, corpo: unknown) =>
    esegui<T>(percorso, { method: 'PATCH', body: JSON.stringify(corpo) }),
  /*  DELETE arriva tardi di proposito: in questo gestionale quasi niente si cancella —
      un'offerta si chiude, un preventivo si annulla, un abbinamento si rifiuta — perché
      quello che è successo va poter essere raccontato dopo. Esiste per le righe di sola
      configurazione, dove non c'è nessuna storia da conservare e una riga sbagliata è
      soltanto una riga sbagliata.  */
  elimina: (percorso: string) => esegui<void>(percorso, { method: 'DELETE' }),
  /*  Un file in uscita: il documento composto.

      Passa da fetch e non da un semplice collegamento perché l'identità viaggia in
      un'intestazione, e un <a href> non la porta: in sviluppo il download tornerebbe 401.
      In cambio si può leggere l'intestazione con i segnaposto sconosciuti, che è quello
      che permette alla schermata di dirlo invece di lasciarlo scoprire nel documento.  */
  scarica: async (percorso: string) => {
    const dichiarato = utenteDichiarato();

    const risposta = await fetch(`${BASE}${percorso}`, {
      credentials: 'include',
      headers: dichiarato ? { [INTESTAZIONE_UTENTE]: dichiarato } : {},
    });

    if (!risposta.ok) {
      const testo = await risposta.text();
      const corpo = testo ? JSON.parse(testo) : null;
      throw new ErroreApi(
        corpo?.messaggio || corpo?.detail || corpo?.title || `Errore ${risposta.status}`,
        risposta.status,
      );
    }

    /*  Il nome esce da Content-Disposition: è quello che il server ha deciso — «Offerta
        26-0001.pdf» — e rifarlo qui vorrebbe dire due posti che lo decidono.  */
    const disposizione = risposta.headers.get('Content-Disposition') ?? '';
    const trovato = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposizione)?.[1];

    return {
      contenuto: await risposta.blob(),
      nomeFile: trovato ? decodeURIComponent(trovato.replace(/"$/, '')) : 'documento',
      sconosciuti: (risposta.headers.get('X-Segnaposti-Sconosciuti') ?? '')
        .split(',')
        .map((x) => x.trim())
        .filter((x) => x.length > 0),
    };
  },

  /** Un file, con il nome che aveva sul disco: serve a riconoscere il distributore. */
  carica: <T>(percorso: string, file: File) => {
    const parti = new FormData();
    parti.append('file', file, file.name);
    return esegui<T>(percorso, { method: 'POST', body: parti });
  },
};
